import { Camera, CalendarDays, Star, MapPin, Video, Users, GraduationCap, Car, Heart, Check } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { AnimatedCounter } from '@/components/AnimatedCounter';
import { CTASection } from '@/components/CTASection';
import { SEO } from '@/components/SEO';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { SITE_IMAGES, SITE_VIDEOS } from '@/lib/siteImages';
import { ABOUT_PAGE } from '@/constants/testIds';

const STATS = [
  { key: 'sessions', icon: Camera, value: '100+', label: 'Sessions' },
  { key: 'years', icon: CalendarDays, value: '3+', label: 'Years Experience' },
  { key: 'rating', icon: Star, value: '5.0', label: 'Client Rating' },
  { key: 'area', icon: MapPin, value: 'DMV', label: 'Based' },
];

const SPECIALTIES = [
  { icon: Camera, label: 'Portrait Sessions' },
  { icon: Video, label: 'Cinematic Videography' },
  { icon: Users, label: 'Events & Celebrations' },
  { icon: GraduationCap, label: 'Graduation Shoots' },
  { icon: Car, label: 'Automotive Photography' },
  { icon: Heart, label: 'Couples & Engagements' },
];

export default function About() {
  const config = useSiteConfig();

  return (
    <div data-testid={ABOUT_PAGE.root}>
      <SEO
        title="About"
        description="Meet Mark Ledezma, founder of LXRA Studios — a DMV-based cinematic photography and videography studio."
      />
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <Reveal>
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-3">About Me</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[0.03em] leading-[0.98] text-[hsl(var(--lxra-fg))]">
                <TextReveal text="THE STORY" />
                <br />
                <TextReveal text="BEHIND THE LENS." delay={0.08} />
              </h1>
              <div className="mt-5 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
              <p className="mt-6 text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed max-w-md">
                We&apos;re a {config.service_area} based photography and videography studio passionate about
                capturing real moments and telling stories through cinematic visuals.
              </p>
              <p className="mt-4 text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed max-w-md">
                Our goal is simple: to create timeless images and videos that you&apos;ll cherish forever.
              </p>
              <blockquote className="mt-8 border-l-2 border-[hsl(var(--lxra-gold))] pl-5 max-w-md">
                <p className="font-display text-lg sm:text-xl text-[hsl(var(--lxra-fg))] leading-snug italic">
                  &ldquo;Every session is a chance to freeze a moment that would otherwise only live in memory.&rdquo;
                </p>
              </blockquote>
              <p className="mt-6 text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed max-w-md">
                From intimate portrait sessions to full-scale productions, every project gets the same
                attention to detail &mdash; thoughtful lighting, genuine direction, and a cinematic eye
                for composition that turns everyday moments into lasting art.
              </p>

              <div className="mt-9 max-w-md">
                <p className="text-xs tracking-[0.18em] uppercase text-[hsl(var(--lxra-gold))] mb-4">What I Bring</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SPECIALTIES.map((item) => (
                    <li key={item.label} className="flex items-center gap-2.5 text-sm text-[hsl(var(--lxra-fg))]">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#141416] border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-gold))]">
                        <item.icon className="h-3.5 w-3.5" />
                      </span>
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-9 text-sm sm:text-base font-semibold text-[hsl(var(--lxra-fg))]" data-testid="about-founder-name">Mark Ledezma</p>
              <p className="text-xs tracking-[0.18em] uppercase text-[hsl(var(--lxra-muted))]">Founder, LXRA Studios</p>

              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 divide-x divide-[hsl(var(--lxra-border))] rounded-2xl border border-[hsl(var(--lxra-border))] overflow-hidden bg-[#0F0F10]">
                {STATS.map((stat) => (
                  <div key={stat.key} className="flex flex-col items-center justify-center gap-2 py-7 px-3 text-center" data-testid={ABOUT_PAGE.statCard(stat.key)}>
                    <stat.icon className="h-5 w-5 text-[hsl(var(--lxra-gold))]" />
                    <p className="font-display text-2xl sm:text-3xl tracking-[0.02em] text-[hsl(var(--lxra-fg))]">
                      <AnimatedCounter value={stat.value} />
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">{stat.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Scroll-pinned editorial media panel — stays in view while the
                story column above scrolls past it on larger screens. */}
            <Reveal delay={0.12} className="lg:sticky lg:top-28 self-start">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[hsl(var(--lxra-border))] lxra-grain">
                <video
                  className="h-full w-full object-cover"
                  src={SITE_VIDEOS.aboutHero}
                  poster={SITE_IMAGES.aboutHero}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  data-testid="about-hero-video"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection
        kicker="Ready to work together?"
        title="LET'S TELL YOUR STORY."
        testId="about-cta-banner"
        buttonTestId={ABOUT_PAGE.bookNow}
      />
    </div>
  );
}
